import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
// Removed Tabs import - using sidebar navigation instead
import { Search, Filter, RefreshCw, CheckCircle, Circle, AlertCircle, BarChart2, CreditCard, Package, Home, Settings, Bell, LogOut, Users, Clock, Mail } from 'lucide-react';
// Removed Header import - not needed for admin dashboard
import TicketList from '../components/admin/TicketList';
import TicketDetail from '../components/admin/TicketDetail';
import DashboardOverview from '../components/admin/DashboardOverview';
import UserManagement from '../components/admin/UserManagement';
import RevenueAnalytics from '../components/admin/RevenueAnalytics';
import PlanManagement from '../components/admin/PlanManagement';
import AffiliateManagement from '../components/admin/AffiliateManagement';
import ContactMessages from '../components/admin/ContactMessages';
import { getTickets, getTicketDetails, updateTicket, addTicketComment } from '../services/adminService';
import api from '../api/api';
import '../styles/admin-enhanced.css';

const Admin1866Page = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [customPlanRequests, setCustomPlanRequests] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      console.warn('No admin token found, redirecting to login');
      navigate('/admin/login');
    }
  }, [navigate]);

  // Fetch custom plan requests
  useEffect(() => {
    const fetchCustomPlanRequests = async () => {
      try {
        const response = await api.get('custom-plan-requests?status=pending');
        
        if (response.data.success) {
          // Handle both possible response structures
          const requests = response.data.data?.requests || response.data.requests || [];
          setCustomPlanRequests(requests);
        } else {
          setCustomPlanRequests([]);
        }
      } catch (err) {
        // Set to empty array on error to prevent UI issues
        setCustomPlanRequests([]);
      }
    };

    fetchCustomPlanRequests();
    // Refresh every 30 seconds
    const interval = setInterval(fetchCustomPlanRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.relative')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Admin logout handler
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Reset ticket selection when changing tabs
    if (tab !== 'tickets' && selectedTicket) {
      setSelectedTicket(null);
    }
  };

  // Fetch tickets from API
  useEffect(() => {
    if (activeTab !== 'tickets') return;
    
    const fetchTickets = async () => {
      setLoading(true);
      try {
        // In a real app, this would use the adminService
        const response = await getTickets({
          page: currentPage,
          limit: 10,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          priority: filterPriority !== 'all' ? filterPriority : undefined,
          category: filterCategory !== 'all' ? filterCategory : undefined,
          search: searchQuery || undefined
        });
        
        if (response.success) {
          setTickets(response.data.tickets);
          setFilteredTickets(response.data.tickets);
          setTotalPages(response.data.pagination.totalPages);
        } else {
          setError(response.error || 'Failed to fetch tickets');
          // Fallback to mock data
          const mockTickets = [
          {
            id: '1866-001',
            title: 'Unable to post job listing',
            description: 'I am trying to post a new job listing but keep getting an error message.',
            status: 'open',
            priority: 'high',
            createdAt: '2025-09-25T10:30:00Z',
            updatedAt: '2025-09-25T10:30:00Z',
            employer: {
              id: 'emp-123',
              name: 'TechCorp Inc.',
              email: 'hr@techcorp.com',
              phone: '+1-555-123-4567'
            },
            category: 'job-posting',
            assignedTo: null,
            comments: [
              {
                id: 'com-001',
                text: "I've tried multiple times but keep getting a 500 error.",
                createdAt: '2025-09-25T10:35:00Z',
                author: 'employer'
              }
            ]
          },
          {
            id: '1866-002',
            title: 'Billing issue with premium subscription',
            description: 'I was charged twice for my premium subscription this month.',
            status: 'in-progress',
            priority: 'medium',
            createdAt: '2025-09-24T14:15:00Z',
            updatedAt: '2025-09-26T09:20:00Z',
            employer: {
              id: 'emp-456',
              name: 'Global Solutions Ltd.',
              email: 'accounts@globalsolutions.com',
              phone: '+1-555-987-6543'
            },
            category: 'billing',
            assignedTo: 'admin-002',
            comments: [
              {
                id: 'com-002',
                text: 'Please check my account statement attached.',
                createdAt: '2025-09-24T14:20:00Z',
                author: 'employer'
              },
              {
                id: 'com-003',
                text: 'We are looking into this issue and will get back to you shortly.',
                createdAt: '2025-09-26T09:20:00Z',
                author: 'admin'
              }
            ]
          },
          {
            id: '1866-003',
            title: 'Cannot access candidate profiles',
            description: "When I try to view candidate profiles, the page doesn't load.",
            status: 'resolved',
            priority: 'high',
            createdAt: '2025-09-23T08:45:00Z',
            updatedAt: '2025-09-27T11:10:00Z',
            employer: {
              id: 'emp-789',
              name: 'Innovate Design Studio',
              email: 'talent@innovatedesign.com',
              phone: '+1-555-456-7890'
            },
            category: 'access',
            assignedTo: 'admin-001',
            comments: [
              {
                id: 'com-004',
                text: 'This is urgent as we need to hire someone by next week.',
                createdAt: '2025-09-23T08:50:00Z',
                author: 'employer'
              },
              {
                id: 'com-005',
                text: "We've identified the issue with our database. Working on a fix.",
                createdAt: '2025-09-26T15:30:00Z',
                author: 'admin'
              },
              {
                id: 'com-006',
                text: 'The issue has been resolved. Please try accessing the profiles again.',
                createdAt: '2025-09-27T11:10:00Z',
                author: 'admin'
              }
            ]
          },
          {
            id: '1866-004',
            title: 'Need to update company information',
            description: 'I need to update our company logo and description but cannot find where to do this.',
            status: 'open',
            priority: 'low',
            createdAt: '2025-09-27T16:20:00Z',
            updatedAt: '2025-09-27T16:20:00Z',
            employer: {
              id: 'emp-101',
              name: 'Sunrise Healthcare',
              email: 'recruitment@sunrisehealth.com',
              phone: '+1-555-222-3333'
            },
            category: 'profile',
            assignedTo: null,
            comments: []
          },
          {
            id: '1866-005',
            title: 'Interview scheduling tool not working',
            description: 'The calendar integration for scheduling interviews is showing errors.',
            status: 'in-progress',
            priority: 'medium',
            createdAt: '2025-09-26T11:05:00Z',
            updatedAt: '2025-09-28T09:15:00Z',
            employer: {
              id: 'emp-202',
              name: 'EduTech Solutions',
              email: 'hr@edutech.org',
              phone: '+1-555-888-9999'
            },
            category: 'tools',
            assignedTo: 'admin-003',
            comments: [
              {
                id: 'com-007',
                text: 'We have several interviews scheduled for tomorrow and need this fixed urgently.',
                createdAt: '2025-09-26T11:10:00Z',
                author: 'employer'
              },
              {
                id: 'com-008',
                text: "We're investigating the calendar API issue. As a temporary solution, you can schedule interviews manually.",
                createdAt: '2025-09-28T09:15:00Z',
                author: 'admin'
              }
            ]
          }
        ];
        
          setTickets(mockTickets);
          setFilteredTickets(mockTickets);
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch tickets. Please try again later.');
        setLoading(false);
      }
    };

    fetchTickets();
  }, [activeTab, currentPage, filterStatus, filterPriority, filterCategory, searchQuery]);

  // Handle ticket selection
  const handleSelectTicket = async (ticket) => {
    try {
      setLoading(true);

      // Fetch detailed ticket data from API
      const response = await getTicketDetails(ticket.id);

      if (response.success && response.data) {
        // API returns ticket in response.data directly
        setSelectedTicket(response.data);

      } else {
        // Fallback to the ticket data we already have

        setSelectedTicket(ticket);
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      // Fallback to the ticket data we already have
      setSelectedTicket(ticket);
    } finally {
      setLoading(false);
    }
  };

  // Handle ticket update
  const handleUpdateTicket = async (updatedTicket) => {

    // Check if this is just a comment update (has comments property)
    if (updatedTicket.comments && Array.isArray(updatedTicket.comments)) {
      // This means we need to reload the ticket to get the updated version
      try {
        const response = await getTicketDetails(updatedTicket.id);
        if (response.success && response.data) {
          setSelectedTicket(response.data);
          // Also refresh the ticket list
          const ticketListResponse = await getTickets({
            page: currentPage,
            limit: 10,
            status: filterStatus !== 'all' ? filterStatus : undefined,
            priority: filterPriority !== 'all' ? filterPriority : undefined,
            category: filterCategory !== 'all' ? filterCategory : undefined,
            search: searchQuery || undefined
          });
          if (ticketListResponse.success) {
            setTickets(ticketListResponse.data.tickets);
            setFilteredTickets(ticketListResponse.data.tickets);
          }
        }
      } catch (err) {
        console.error('Error refreshing ticket:', err);
      }
      return;
    }
    
    // Otherwise, update status/priority/assignment
    try {
      const response = await updateTicket(updatedTicket.id, {
        status: updatedTicket.status,
        priority: updatedTicket.priority,
        assignedTo: updatedTicket.assignedTo
      });
      
      if (response.success) {

        // Update the ticket in the list
        const updatedTickets = tickets.map(ticket => 
          ticket.id === updatedTicket.id ? {...ticket, ...updatedTicket} : ticket
        );
        setTickets(updatedTickets);
        setFilteredTickets(updatedTickets.filter(ticket => 
          filterStatus === 'all' || ticket.status === filterStatus
        ));
        setSelectedTicket(updatedTicket);
      } else {
        console.error('Failed to update ticket:', response.error);
      }
    } catch (err) {
      console.error('Error updating ticket:', err);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setLoading(true);
    setCurrentPage(1);
    setSearchQuery('');
    setFilterStatus('all');
    setFilterPriority('all');
    setFilterCategory('all');
    
    // This will trigger the useEffect to fetch tickets again
    setTimeout(() => {
      setLoading(false);
    }, 300);
  };
  
  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      
      {/* Enhanced Admin Header with Gradient and Better Design */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-6 px-6 shadow-2xl relative">
        {/* Decorative Background Pattern */}
        <div className="absolute inset-0 opacity-10 overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 translate-y-48"></div>
        </div>
        
        <div className="container mx-auto flex justify-between items-center relative z-10">
          <div className="flex items-center space-x-4">
            <div className="bg-white p-3 rounded-xl shadow-lg hover:scale-110 transition-transform duration-300">
              <Home size={24} className="text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Admin Control Center</h1>
              <p className="text-blue-100 text-sm mt-1">Manage your platform with ease</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            {/* Notifications */}
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative hover:scale-110 transition-transform duration-200"
              >
                <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm hover:bg-white/30 transition-colors">
                  <Bell size={22} />
                  {customPlanRequests.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg animate-pulse">
                      {customPlanRequests.length}
                    </span>
                  )}
                </div>
              </button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] max-h-[500px] overflow-hidden"
                >
                  <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                      <Bell size={18} className="text-blue-600" />
                      Custom Plan Requests
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                      {customPlanRequests.length} pending request{customPlanRequests.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  
                  <div className="max-h-[400px] overflow-y-auto">
                    {customPlanRequests.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Bell size={32} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No pending requests</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {customPlanRequests.map((request, index) => (
                          <div key={request.id} className="p-4 hover:bg-blue-50 transition-colors cursor-pointer">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-bold text-gray-900 text-base">{request.companyName}</h4>
                                <p className="text-sm text-gray-600">{request.contactName}</p>
                              </div>
                              <span className="px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                                NEW
                              </span>
                            </div>
                            <div className="text-sm text-gray-700 space-y-1.5 mt-3">
                              <div className="flex items-start gap-2">
                                <span className="text-gray-500 min-w-[90px]">Email:</span>
                                <span className="font-medium text-blue-600">{request.email}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="text-gray-500 min-w-[90px]">Phone:</span>
                                <span className="font-medium">{request.phone}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="text-gray-500 min-w-[90px]">Company Size:</span>
                                <span className="font-medium">{request.companySize}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="text-gray-500 min-w-[90px]">Job Postings:</span>
                                <span className="font-medium">{request.jobPostings}/month</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="text-gray-500 min-w-[90px]">Recruiters:</span>
                                <span className="font-medium">{request.recruiters}</span>
                              </div>
                              {request.message && (
                                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600 italic border-l-2 border-blue-400">
                                  "{request.message}"
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                              <Clock size={12} />
                              {new Date(request.createdAt).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
            
            {/* Admin Profile */}
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg hover:bg-white/30 transition-colors cursor-pointer">
              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
                <span className="font-bold text-white text-lg">A</span>
              </div>
              <div className="ml-3 text-left">
                <p className="font-semibold text-sm">Admin User</p>
                <p className="text-xs text-blue-100">Administrator</p>
              </div>
            </div>
            
            {/* Settings */}
            <button className="bg-white/20 p-2 rounded-lg backdrop-blur-sm hover:bg-white/30 transition-colors hover:rotate-90 duration-300">
              <Settings size={22} />
            </button>
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
              title="Logout"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
      
      <main className="flex-1 flex">
        {/* Left Sidebar Navigation */}
        <div className="w-64 bg-white border-r border-gray-200 p-6">
          <nav className="space-y-2">
            {/* Dashboard */}
            <button
              onClick={() => handleTabChange('dashboard')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === 'dashboard'
                  ? 'bg-blue-50 text-blue-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                activeTab === 'dashboard'
                  ? 'bg-blue-100'
                  : 'bg-gray-100'
              }`}>
                <BarChart2 size={20} className={activeTab === 'dashboard' ? 'text-blue-600' : 'text-gray-600'} />
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-semibold">Dashboard</div>
                <div className="text-xs text-gray-500">Overview & Stats</div>
              </div>
            </button>

            {/* Users */}
            <button
              onClick={() => handleTabChange('users')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === 'users'
                  ? 'bg-green-50 text-green-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                activeTab === 'users'
                  ? 'bg-green-100'
                  : 'bg-gray-100'
              }`}>
                <Users size={20} className={activeTab === 'users' ? 'text-green-600' : 'text-gray-600'} />
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-semibold">Users</div>
                <div className="text-xs text-gray-500">Manage Accounts</div>
              </div>
            </button>

            {/* Revenue */}
            <button
              onClick={() => handleTabChange('revenue')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === 'revenue'
                  ? 'bg-purple-50 text-purple-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                activeTab === 'revenue'
                  ? 'bg-purple-100'
                  : 'bg-gray-100'
              }`}>
                <CreditCard size={20} className={activeTab === 'revenue' ? 'text-purple-600' : 'text-gray-600'} />
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-semibold">Revenue</div>
                <div className="text-xs text-gray-500">Financial Analytics</div>
              </div>
            </button>

            {/* Tickets */}
            <button
              onClick={() => handleTabChange('tickets')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === 'tickets'
                  ? 'bg-amber-50 text-amber-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                activeTab === 'tickets'
                  ? 'bg-amber-100'
                  : 'bg-gray-100'
              }`}>
                <AlertCircle size={20} className={activeTab === 'tickets' ? 'text-amber-600' : 'text-gray-600'} />
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-semibold">Tickets</div>
                <div className="text-xs text-gray-500">Support Queue</div>
              </div>
            </button>

            {/* Plans */}
            <button
              onClick={() => handleTabChange('plans')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === 'plans'
                  ? 'bg-indigo-50 text-indigo-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                activeTab === 'plans'
                  ? 'bg-indigo-100'
                  : 'bg-gray-100'
              }`}>
                <Package size={20} className={activeTab === 'plans' ? 'text-indigo-600' : 'text-gray-600'} />
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-semibold">Plans</div>
                <div className="text-xs text-gray-500">Manage subscriptions</div>
              </div>
            </button>

            {/* Affiliates */}
            <button
              onClick={() => handleTabChange('affiliates')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === 'affiliates'
                  ? 'bg-pink-50 text-pink-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                activeTab === 'affiliates'
                  ? 'bg-pink-100'
                  : 'bg-gray-100'
              }`}>
                <Users size={20} className={activeTab === 'affiliates' ? 'text-pink-600' : 'text-gray-600'} />
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-semibold">Affiliates</div>
                <div className="text-xs text-gray-500">Manage affiliate program</div>
              </div>
            </button>

            <button
              onClick={() => handleTabChange('contact')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                activeTab === 'contact'
                  ? 'bg-teal-50 text-teal-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className={`p-2 rounded-lg ${
                activeTab === 'contact'
                  ? 'bg-teal-100'
                  : 'bg-gray-100'
              }`}>
                <Mail size={20} className={activeTab === 'contact' ? 'text-teal-600' : 'text-gray-600'} />
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-semibold">Contact Messages</div>
              </div>
            </button>
          </nav>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 bg-gray-50 p-8 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            key={activeTab}
          >
            {/* Dashboard Content */}
            {activeTab === 'dashboard' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                  <p className="text-gray-500 text-sm mt-1">Real-time platform insights and analytics</p>
                </div>
                <DashboardOverview />
              </div>
            )}

            {/* Users Content */}
            {activeTab === 'users' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage user accounts and permissions</p>
                </div>
                <UserManagement />
              </div>
            )}

            {/* Revenue Content */}
            {activeTab === 'revenue' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900">Revenue Analytics</h1>
                  <p className="text-gray-500 text-sm mt-1">Financial performance and insights</p>
                </div>
                <RevenueAnalytics />
              </div>
            )}

            {/* Tickets Content */}
            {activeTab === 'tickets' && (
              <div>
                <div className="mb-6 flex justify-between items-center">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Support Tickets</h1>
                    <p className="text-gray-500 text-sm mt-1">Manage customer support requests</p>
                  </div>
                  <button 
                    onClick={handleRefresh}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200"
                  >
                    <RefreshCw size={18} />
                    <span>Refresh</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left column - Ticket list */}
                  <div className="lg:col-span-1 bg-white rounded-xl shadow-md border border-gray-100 p-4 overflow-hidden">
                    <div className="mb-4">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search tickets..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                        <Search className="absolute left-3 top-3.5 text-blue-500" size={18} />
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Filters</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-xs text-gray-500 mb-1">Status</label>
                          <div className="relative">
                            <select
                              value={filterStatus}
                              onChange={(e) => setFilterStatus(e.target.value)}
                              className="w-full appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="all">All Status</option>
                              <option value="open">Open</option>
                              <option value="in-progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                            <Filter size={14} className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-xs text-gray-500 mb-1">Priority</label>
                          <select
                            value={filterPriority}
                            onChange={(e) => setFilterPriority(e.target.value)}
                            className="w-full appearance-none bg-white border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="all">All Priority</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>
                        
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-xs text-gray-500 mb-1">Category</label>
                          <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="w-full appearance-none bg-white border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="all">All Categories</option>
                            <option value="job-posting">Job Posting</option>
                            <option value="billing">Billing</option>
                            <option value="access">Access</option>
                            <option value="profile">Profile</option>
                            <option value="tools">Tools</option>
                            <option value="technical">Technical</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg overflow-hidden">
                      <TicketList 
                        tickets={filteredTickets} 
                        selectedTicketId={selectedTicket?.id}
                        onSelectTicket={handleSelectTicket}
                        loading={loading}
                        error={error}
                      />
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex justify-center mt-6">
                        <nav className="relative z-0 inline-flex rounded-lg shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-3 py-2 rounded-l-lg border ${currentPage === 1 
                              ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed' 
                              : 'border-blue-300 bg-white text-blue-600 hover:bg-blue-50'} text-sm font-medium transition-colors`}
                          >
                            Previous
                          </button>
                          {[...Array(totalPages)].map((_, i) => (
                            <button
                              key={i}
                              onClick={() => handlePageChange(i + 1)}
                              className={`relative inline-flex items-center px-4 py-2 border ${currentPage === i + 1
                                ? 'z-10 bg-blue-600 border-blue-600 text-white'
                                : 'bg-white border-gray-200 text-gray-700 hover:bg-blue-50 hover:text-blue-600'} text-sm font-medium transition-colors`}
                            >
                              {i + 1}
                            </button>
                          ))}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-3 py-2 rounded-r-lg border ${currentPage === totalPages 
                              ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed' 
                              : 'border-blue-300 bg-white text-blue-600 hover:bg-blue-50'} text-sm font-medium transition-colors`}
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    )}
                  </div>

                  {/* Right column - Ticket details */}
                  <div className="lg:col-span-2 bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    {selectedTicket ? (
                      <div>
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
                          <h3 className="font-medium text-gray-800">Ticket Details</h3>
                        </div>
                        <div className="p-4">
                          <TicketDetail 
                            ticket={selectedTicket} 
                            onUpdateTicket={handleUpdateTicket} 
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-96 text-gray-400 bg-gray-50 bg-opacity-50">
                        <AlertCircle size={64} className="mb-4 text-blue-300" />
                        <p className="text-lg font-medium">Select a ticket to view details</p>
                        <p className="text-sm text-gray-500 mt-2">Choose from the list on the left</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Plans Content */}
            {activeTab === 'plans' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
                  <p className="text-gray-600 mt-2">Manage subscription plans and pricing</p>
                </div>
                <PlanManagement />
              </div>
            )}

            {/* Affiliates Content */}
            {activeTab === 'affiliates' && (
              <div>
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-900">Affiliate Program</h1>
                  <p className="text-gray-600 mt-2">Manage affiliates, approvals, and payouts</p>
                </div>
                <AffiliateManagement />
              </div>
            )}

            {/* Contact Messages Content */}
            {activeTab === 'contact' && (
              <div>
                <ContactMessages />
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Admin1866Page;
